import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthContext.jsx';
import { useCanAsMember, useDocumentTitle } from '@/hooks';
import { PageHead, useConfirm, useToast, Skel } from '@/ui';
import { annulTaxDocument, findTaxDocumentsByFolio } from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import { StatusDotPill } from '@/shared';
import { formatCLP, formatChileanDateTime } from '@/lib';

import './PlataformaPagos.css';
import './PlataformaDocumentos.css';

// Documentos · Plataforma (backoffice). Annuls an emitted factura with a credit
// note for what remains of it (REC-1934). The operator searches by folio, picks
// the factura, reads the dry run and only then executes with a reason:
//   · the folio finds documents and the annulment takes the id, because a
//     folio is unique per issuer and DTE type, not a key;
//   · the dry run is what the API would do, so the preview is never a guess;
//   · executing twice does not annul twice: the API answers the annulment
//     that already exists.

const fmtDate = (iso) => formatChileanDateTime(iso) || '—';

// The two reasons the SII maps to CodRef 1 (anula). Both annul; the number is
// what the credit note records.
const MOTIVOS = [
  { value: 1, label: 'Error de digitación' },
  { value: 2, label: 'Reclamo del cliente' },
];

// Every motivo a credit note can carry (SimpleFactura ReasonType in
// recepthor-api), so a note emitted with one this screen does not offer still
// reads by name.
const MOTIVO_LABEL = {
  1: 'Error de digitación',
  2: 'Reclamo del cliente',
  3: 'Datos desactualizados',
  4: 'Intereses de mora',
  5: 'Intereses por cambio de fecha',
  6: 'Otros',
};
const motivoLabel = (m) =>
  m == null ? '—' : (MOTIVO_LABEL[m] ?? `Motivo ${m}`);

// TaxDocumentStatus in recepthor-api, as the operator reads it.
const STATUS_LABEL = {
  DRAFT: 'Borrador',
  EMITTING: 'En emisión',
  EMITTED: 'Emitida',
  FAILED: 'Fallida',
  VOID: 'Descartada',
  ABANDONED: 'Abandonada',
};
const statusLabel = (s) => STATUS_LABEL[s] ?? s ?? '—';

// Motivos that annul the factura before the SII (the rest correct amounts or
// text). Same rule as currentAnnulment in recepthor-api.
const ANNULMENT_MOTIVOS = new Set([1, 2]);

// Whether the factura is annulled before the SII: an emitted annulment note
// among the credit notes the folio search brings (REC-1957), or, from the
// review or the annulment, one already there or the one just created once it
// was emitted (a `created` note can come back FAILED or DRAFT).
// `no_remaining_cap` is not one: the amount went to amount or text
// corrections, not to an annulment.
function isAnnulled(doc, shown) {
  const emittedAnnulment = (doc.creditNotes ?? []).some(
    (n) => ANNULMENT_MOTIVOS.has(n.motivo) && n.status === 'EMITTED',
  );
  if (emittedAnnulment || shown?.outcome === 'already_annulled') return true;
  return shown?.outcome === 'created' && shown.creditNote?.status === 'EMITTED';
}

// One line per credit note: folio, motivo, status and amount by name.
function CreditNotesList({ notes, labelledBy }) {
  return (
    <ul className="pld-corrections" aria-labelledby={labelledBy}>
      {notes.map((c) => (
        <li key={c.id} className="plp-cf__s">
          Nota de crédito {c.folio ?? 'sin asignar'} · {motivoLabel(c.motivo)} ·{' '}
          {statusLabel(c.status)} · {formatCLP(c.totalAmount)}
        </li>
      ))}
    </ul>
  );
}

const REASON_MIN = 3;
const REASON_MAX = 200;

// What each outcome means to the operator, as one line with its tone.
const OUTCOME = {
  would_emit: { tone: 'warn', label: 'Se puede anular' },
  created: { tone: 'ok', label: 'Anulada' },
  already_annulled: { tone: 'ok', label: 'Ya estaba anulada' },
  annulment_in_flight: { tone: 'pay', label: 'Anulación en curso' },
  annulment_abandoned: { tone: 'err', label: 'Anulación abandonada' },
  no_remaining_cap: { tone: 'info', label: 'Sin saldo para anular' },
};

const OUTCOME_TEXT = {
  would_emit:
    'Anular emite una nota de crédito por el saldo y deja la factura sin efecto ante el SII.',
  created:
    'La nota de crédito se creó. Si todavía no está emitida, el barrido de documentos la reintenta.',
  already_annulled:
    'Esta factura ya tiene su nota de crédito de anulación emitida: no se crea otra.',
  annulment_in_flight:
    'Hay una nota de crédito de anulación que todavía no llega al SII. Se reintenta sola: no hace falta otra.',
  annulment_abandoned:
    'La anulación anterior llegó a su límite de intentos. Hay que revisarla con el equipo de desarrollo.',
  no_remaining_cap:
    'Otras notas de crédito ya usaron todo el monto de la factura.',
};

// A folio is a nonzero integer: positive as the SII assigns it, negative under
// Beta's mock issuer. It is stored as int4, so the API takes the int4 range and
// anything outside it never reaches the API.
const FOLIO_MIN = -2_147_483_648;
const FOLIO_MAX = 2_147_483_647;

function parseFolio(raw) {
  const t = String(raw).trim();
  if (!/^-?\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= FOLIO_MIN && n <= FOLIO_MAX && n !== 0
    ? n
    : null;
}

function Field({ label, value, mono }) {
  return (
    <div className="plp-cf">
      <span className="plp-cf__l">{label}</span>
      <span className={mono ? 'plp-cf__s' : 'plp-cf__v'}>{value || '—'}</span>
    </div>
  );
}

// The organization by its name, with its id underneath. Without a profile
// (name null) or from an API that does not send it yet, the id alone.
function OrganizationField({ doc }) {
  const name = doc.organization?.name;
  return (
    <div className="plp-cf">
      <span className="plp-cf__l">Organización</span>
      {name ? (
        <>
          <span className="plp-cf__v">{name}</span>
          <span className="plp-cf__s">{doc.organizationId}</span>
        </>
      ) : (
        <span className="plp-cf__s">{doc.organizationId || '—'}</span>
      )}
    </div>
  );
}

function errorText(error) {
  return error?.message ?? 'Error desconocido.';
}

function DocumentCard({ doc, organizationId, onAnnulled }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [motivo, setMotivo] = useState(MOTIVOS[0].value);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  // True while the confirm dialog is open: the card is locked so nothing the
  // operator confirms can change underneath the dialog.
  const [confirming, setConfirming] = useState(false);
  // Sync guard against a double click before React paints the disabled state.
  const runningRef = useRef(false);
  // Each review gets a number, and executing bumps it: a review that comes
  // back after a newer review, a motivo change or an execution is dropped.
  const reviewSeq = useRef(0);

  const dryRunMut = useMutation({
    mutationFn: ({ motivo: m }) =>
      annulTaxDocument(doc.id, { motivo: m }, { organizationId }),
    onSuccess: (data, { motivo: m, seq }) => {
      if (seq === reviewSeq.current) setPreview({ ...data, motivo: m });
    },
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo revisar la anulación',
        message: errorText(e),
      }),
  });

  const executeMut = useMutation({
    // Executes exactly what the dialog showed, passed in by onExecute.
    mutationFn: ({ motivo: m, reason: why }) =>
      annulTaxDocument(
        doc.id,
        { dryRun: false, motivo: m, reason: why },
        { organizationId },
      ),
    onSuccess: (data) => {
      reviewSeq.current += 1;
      setResult(data);
      setPreview(null);
      setReason('');
      toast({
        kind: data.outcome === 'created' ? 'success' : 'info',
        message: OUTCOME[data.outcome]?.label ?? data.outcome,
      });
      onAnnulled();
    },
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo anular la factura',
        message: errorText(e),
      }),
    onSettled: () => {
      runningRef.current = false;
    },
  });

  // A preview answers for one motivo; a different one is a different
  // question, so the old answer is dropped rather than executed.
  const changeMotivo = (value) => {
    reviewSeq.current += 1;
    setMotivo(value);
    setPreview(null);
  };

  const review = () => {
    reviewSeq.current += 1;
    setResult(null);
    setPreview(null);
    dryRunMut.mutate({ motivo, seq: reviewSeq.current });
  };

  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;
  const locked = confirming || dryRunMut.isPending || executeMut.isPending;
  const canExecute =
    preview?.outcome === 'would_emit' &&
    preview.motivo === motivo &&
    reasonOk &&
    !locked;

  const onExecute = useCallback(async () => {
    if (!canExecute || runningRef.current) return;
    runningRef.current = true;
    const seq = reviewSeq.current;
    setConfirming(true);
    const ok = await confirm({
      title: `Anular la factura folio ${doc.folio}`,
      body: (
        <>
          Se emite una nota de crédito por{' '}
          <b>{formatCLP(preview.wouldEmit?.totalAmount)}</b> que anula la
          factura ante el SII. No se puede deshacer.
          <br />
          <br />
          Motivo: <b>{MOTIVOS.find((m) => m.value === motivo)?.label}</b>.
          Razón: {trimmed}
        </>
      ),
      confirmLabel: 'Anular la factura',
      confirmVariant: 'seal',
    });
    setConfirming(false);
    // A review or motivo change since the dialog opened voids the confirmation.
    if (ok && seq === reviewSeq.current) {
      executeMut.mutate({ motivo, reason: trimmed });
    } else {
      runningRef.current = false;
    }
  }, [canExecute, confirm, doc.folio, executeMut, motivo, preview, trimmed]);

  const shown = result ?? preview;
  const st = shown ? OUTCOME[shown.outcome] : null;
  const annulled = isAnnulled(doc, shown);
  // One list of credit notes: the review's or the annulment's when it has
  // any (fresher: it carries the note just created), otherwise the search's
  // (REC-1957; an older API answer has none).
  const searchNotes = Array.isArray(doc.creditNotes) ? doc.creditNotes : [];
  const notes = shown?.corrections?.length ? shown.corrections : searchNotes;
  const notesLabelId = `notes-label-${doc.id}`;

  return (
    <article className="plp-card is-open">
      <div className="plp-card__head">
        <div className="plp-card__lead">
          <span className="plp-date">Folio {doc.folio}</span>
          {annulled ? (
            <StatusDotPill status="ok" label="Anulada" />
          ) : (
            <StatusDotPill status="ok" label="Emitida" />
          )}
          <span className="plp-cf__v">{fmtDate(doc.emittedAt)}</span>
        </div>
        <div className="plp-card__right">
          <span className="plp-money">{formatCLP(doc.totalAmount)}</span>
        </div>
      </div>
      <div className="plp-card__fields">
        <OrganizationField doc={doc} />
        <Field label="RUT receptor" value={doc.receiverRut} mono />
        <Field label="Neto" value={formatCLP(doc.netAmount)} />
        <Field label="IVA" value={formatCLP(doc.taxAmount)} />
      </div>
      {notes.length > 0 && (
        <div className="pld-notes">
          <span id={notesLabelId} className="plp-cf__l">
            Notas de crédito
          </span>
          <CreditNotesList notes={notes} labelledBy={notesLabelId} />
        </div>
      )}

      <div className="pld-annul">
        <fieldset className="pld-motivo" disabled={locked}>
          <legend className="plp-cf__l">Motivo</legend>
          {MOTIVOS.map((m) => (
            <label key={m.value} className="pld-motivo__opt">
              <input
                type="radio"
                name={`motivo-${doc.id}`}
                value={m.value}
                checked={motivo === m.value}
                onChange={() => changeMotivo(m.value)}
              />
              {m.label}
            </label>
          ))}
        </fieldset>

        <button
          type="button"
          className="plp-rowbtn plp-rowbtn--ghost"
          disabled={locked}
          onClick={review}
        >
          {dryRunMut.isPending ? 'Revisando…' : 'Revisar anulación'}
        </button>
      </div>

      <div className="pld-result-live" aria-live="polite">
        {shown && (
          <div className="pld-result">
            <div className="pld-outcome">
              <StatusDotPill
                status={st?.tone ?? 'info'}
                label={st?.label ?? shown.outcome}
              />
              <span>{OUTCOME_TEXT[shown.outcome]}</span>
            </div>
            <div className="plp-card__fields">
              <Field
                label="Saldo anulable"
                value={formatCLP(shown.remainingCap)}
              />
              {shown.wouldEmit && (
                <>
                  <Field
                    label="Nota de crédito: neto"
                    value={formatCLP(shown.wouldEmit.netAmount)}
                  />
                  <Field
                    label="Nota de crédito: IVA"
                    value={formatCLP(shown.wouldEmit.taxAmount)}
                  />
                  <Field
                    label="Nota de crédito: total"
                    value={formatCLP(shown.wouldEmit.totalAmount)}
                  />
                </>
              )}
              {shown.creditNote && (
                <Field
                  label="Nota de crédito"
                  value={`Folio ${shown.creditNote.folio ?? 'sin asignar'} · ${statusLabel(shown.creditNote.status)} · ${formatCLP(shown.creditNote.totalAmount)}`}
                />
              )}
            </div>

            {preview?.outcome === 'would_emit' && (
              <div className="pld-execute">
                <label className="pld-reason">
                  <span className="plp-cf__l">
                    Razón (queda en el registro de auditoría)
                  </span>
                  <textarea
                    rows={2}
                    maxLength={REASON_MAX}
                    aria-describedby={`reason-hint-${doc.id}`}
                    value={reason}
                    disabled={locked}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Por ejemplo: cobro de prueba del equipo"
                  />
                  <span id={`reason-hint-${doc.id}`} className="plp-cf__s">
                    De {REASON_MIN} a {REASON_MAX} caracteres · {trimmed.length}
                    /{REASON_MAX}
                  </span>
                </label>
                <button
                  type="button"
                  className="plp-rowbtn"
                  disabled={!canExecute}
                  onClick={onExecute}
                >
                  {executeMut.isPending ? 'Anulando…' : 'Anular'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export default function PlataformaDocumentos() {
  useDocumentTitle('Documentos · Plataforma');
  const { setBreadcrumb, setPills } = useOutletContext();
  const organizationId = useAuth()?.organizationId ?? null;
  // The API lets an owner through without the stored permission.
  const canAnnul = useCanAsMember('payout:manual');
  const queryClient = useQueryClient();

  const [input, setInput] = useState('');
  const [folio, setFolio] = useState(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setBreadcrumb(['Documentos']);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);

  const found = useQuery({
    queryKey: qk.taxDocumentsByFolio.byFolio(folio),
    queryFn: () => findTaxDocumentsByFolio(folio, { organizationId }),
    enabled: canAnnul && folio != null && organizationId != null,
  });
  const documents = Array.isArray(found.data?.documents)
    ? found.data.documents
    : [];

  const onSearch = (e) => {
    e.preventDefault();
    const parsed = parseFolio(input);
    setInvalid(parsed == null);
    // A rejected folio also drops the previous results: the cards below must
    // always belong to the folio in the box, never to an earlier search.
    setFolio(parsed);
  };

  const onAnnulled = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: qk.taxDocumentsByFolio.all(),
      }),
    [queryClient],
  );

  if (!canAnnul) {
    return (
      <div className="page reveal">
        <PageHead kicker="Plataforma" title="Documentos" />
        <div role="alert" className="plp-empty is-err">
          No tienes el permiso para anular documentos (payout:manual).
        </div>
      </div>
    );
  }

  return (
    <div className="page reveal">
      <PageHead
        kicker="Plataforma"
        title="Documentos"
        sub={<>Anular una factura emitida, buscándola por su folio</>}
      />

      <form className="pld-search" onSubmit={onSearch} noValidate>
        <label className="pld-search__field">
          <span className="plp-cf__l">Folio</span>
          <input
            inputMode="numeric"
            value={input}
            aria-invalid={invalid}
            aria-describedby={invalid ? 'folio-error' : undefined}
            onChange={(e) => setInput(e.target.value)}
            placeholder="13"
          />
        </label>
        <button type="submit" className="plp-rowbtn">
          Buscar
        </button>
        {invalid && (
          <span id="folio-error" role="alert" className="plp-cf__s is-err">
            El folio es un número entero distinto de 0.
          </span>
        )}
      </form>

      {folio != null && organizationId == null && (
        <div role="alert" className="plp-empty is-err">
          No hay una organización activa: elige la organización de la plataforma
          para buscar.
        </div>
      )}

      {folio != null && organizationId != null && (
        <section className="plp-franja">
          {found.isPending && (
            <div className="plp-cards">
              <div className="plp-card">
                <div className="plp-card__head">
                  <Skel w={90} />
                  <Skel w={70} />
                </div>
              </div>
            </div>
          )}
          {found.isError && (
            <div className="plp-empty is-err">
              No se pudo buscar el folio: {errorText(found.error)}{' '}
              <button
                type="button"
                className="plp-morebtn"
                onClick={() => found.refetch()}
              >
                Reintentar
              </button>
            </div>
          )}
          {found.isSuccess && documents.length === 0 && (
            <div className="plp-empty">
              Ninguna factura emitida tiene el folio {folio}.
            </div>
          )}
          {found.isSuccess && documents.length > 1 && (
            <div className="plp-franja__cap">
              <b>{documents.length} facturas</b> tienen el folio {folio}: revisa
              la organización antes de anular.
            </div>
          )}
          {/* A failed refetch after annulling keeps the cards and their result. */}
          {documents.length > 0 && (
            <div className="plp-cards">
              {documents.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  organizationId={organizationId}
                  onAnnulled={onAnnulled}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
